import type { ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { systemClock } from '@sakhtar/calendar';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { baseOrganizationPayloadOf, setupStepView, type SetupView } from '../../lib/types/setup.js';
import { formatRialFa, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  BalanceSheetResponse,
  IncomeStatementResponse,
  LedgerResponse,
  TrialBalanceColumnAmounts,
  TrialBalanceColumnsResponse,
} from '../../lib/types/finance.js';
import { COLUMN_GROUPS } from '../finance/TrialBalanceColumnsPage.js';
import { signedBalanceLabel } from '../finance/LedgerPage.js';
import { signedRial } from '../finance/FinancialStatementsPages.js';
import { isReady, PrintLoadState, PrintPageShell, TenantIdentityBlock } from './PrintChrome.js';

/** Bumped whenever a finance print layout changes (SOT §12.12: template version on every output). */
const FINANCE_PRINT_TEMPLATE_VERSION = 'v1';

const cell = { padding: '5px 4px' } as const;
const num = { ...cell, textAlign: 'left' } as const;
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 11.5 } as const;

/**
 * Shared frame for the finance report print views: tenant identity, title,
 * period lines, generation date and template version, then the report body.
 * Same PrintPageShell chrome as TrialBalancePrintPage.tsx.
 */
function FinancePrintFrame<T>(props: {
  readonly title: string;
  readonly backPath: string;
  readonly backLabel: string;
  readonly path: string | null;
  readonly lines: (data: T) => readonly string[];
  readonly body: (data: T, unit: AmountDisplayUnit, label: string) => ReactNode;
}) {
  const { unit, label, ready } = useAmountDisplay();
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const report = useApiGet<T>(slug && props.path ? `${base}${props.path}` : null, [
    slug,
    props.path,
  ]);
  const setup = useApiGet<SetupView>(slug ? `${base}/api/v1/setup` : null, [slug]);
  if (!slug) return null;
  const backHref = `${base}${props.backPath}`;
  if (!ready || !props.path || !isReady(report)) {
    return (
      <PrintPageShell backHref={backHref} backLabel={props.backLabel}>
        <PrintLoadState error={report.status === 'error' ? report.error : undefined} />
      </PrintPageShell>
    );
  }
  const baseOrg = isReady(setup)
    ? baseOrganizationPayloadOf(setupStepView(setup.data, 'base-organization'))
    : null;
  return (
    <PrintPageShell backHref={backHref} backLabel={props.backLabel}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 12.5 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
          <TenantIdentityBlock
            legalIdentity={baseOrg?.legalIdentity ?? null}
            primaryLocation={baseOrg?.primaryLocation ?? null}
            personType={baseOrg?.personType ?? null}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
            <span style={{ fontSize: 18, fontWeight: 700 }}>{props.title}</span>
            {props.lines(report.data).map((line) => (
              <span key={line}>{line}</span>
            ))}
            <span>تاریخ تهیه {displayJalaliDate(systemClock.today())}</span>
          </div>
        </div>
        {props.body(report.data, unit, label)}
        <p style={{ fontSize: 10.5, color: '#666', margin: 0 }}>
          ارقام به {label} · فقط اسناد قطعی · قالب {FINANCE_PRINT_TEMPLATE_VERSION}
        </p>
      </div>
    </PrintPageShell>
  );
}

function rangeLine(from: string | null, to: string | null): string {
  if (from && to) return `از ${displayJalaliDate(from)} تا ${displayJalaliDate(to)}`;
  if (from) return `از ${displayJalaliDate(from)}`;
  if (to) return `تا ${displayJalaliDate(to)}`;
  return 'از ابتدا تا امروز';
}

function useQuery(keys: readonly string[]): { query: string; values: Record<string, string> } {
  const [params] = useSearchParams();
  const values: Record<string, string> = {};
  const q = new URLSearchParams();
  for (const k of keys) {
    const v = params.get(k);
    if (v) {
      values[k] = v;
      q.set(k, v);
    }
  }
  return { query: q.toString(), values };
}

const LEDGER_TITLE: Record<string, string> = {
  general: 'دفتر کل',
  subsidiary: 'دفتر معین',
  detail: 'دفتر تفصیلی',
};

/** دفتر کل / معین / تفصیلی — same query string as LedgerPage. */
export function LedgerPrintPage() {
  const { query, values } = useQuery(['accountRef', 'dimensionValueId', 'fromDate', 'toDate']);
  const [params] = useSearchParams();
  const mode = params.get('mode') ?? 'subsidiary';
  const ok = Boolean(values.accountRef || values.dimensionValueId);
  return (
    <FinancePrintFrame<LedgerResponse>
      title={LEDGER_TITLE[mode] ?? 'دفتر'}
      backPath={`/finance/reports/ledger?mode=${mode}`}
      backLabel="بازگشت به دفاتر"
      path={ok ? `/api/v1/finance/reports/ledger?${query}` : null}
      lines={(d) => [
        d.dimensionValue
          ? `${d.dimensionValue.dimensionName}: ${d.dimensionValue.code} — ${d.dimensionValue.name}`
          : d.account
            ? `${d.account.code} — ${d.account.name}`
            : '',
        rangeLine(d.fromDate, d.toDate),
      ]}
      body={(d, unit) => (
        <table style={tableStyle}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333' }}>
              <th style={{ ...cell, textAlign: 'right' }}>تاریخ</th>
              <th style={{ ...cell, textAlign: 'right' }}>سند</th>
              <th style={{ ...cell, textAlign: 'right' }}>حساب</th>
              <th style={{ ...cell, textAlign: 'right' }}>شرح</th>
              <th style={num}>بدهکار</th>
              <th style={num}>بستانکار</th>
              <th style={num}>مانده</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #e2e2e2' }}>
              <td style={cell} colSpan={6}>
                مانده از قبل
              </td>
              <td className="sk-num" style={num}>
                {signedBalanceLabel(d.openingBalanceRial, unit)}
              </td>
            </tr>
            {d.rows.map((r) => (
              <tr
                key={`${r.journalId}-${r.rowNumber}`}
                style={{ borderBottom: '1px solid #e2e2e2' }}
              >
                <td className="sk-num" style={cell}>
                  {displayJalaliDate(r.businessDate)}
                </td>
                <td className="ltr sk-num" style={cell}>
                  {r.documentNumber}
                </td>
                <td style={cell}>
                  {r.accountRef} {r.accountName}
                </td>
                <td style={cell}>{r.description ?? ''}</td>
                <td className="sk-num" style={num}>
                  {r.debitRial !== '0' ? formatRialFa(r.debitRial, unit) : '—'}
                </td>
                <td className="sk-num" style={num}>
                  {r.creditRial !== '0' ? formatRialFa(r.creditRial, unit) : '—'}
                </td>
                <td className="sk-num" style={num}>
                  {signedBalanceLabel(r.balanceRial, unit)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
              <td style={cell} colSpan={4}>
                جمع
              </td>
              <td className="sk-num" style={num}>
                {formatRialFa(d.totals.debitRial, unit)}
              </td>
              <td className="sk-num" style={num}>
                {formatRialFa(d.totals.creditRial, unit)}
              </td>
              <td className="sk-num" style={num}>
                {signedBalanceLabel(d.totals.closingBalanceRial, unit)}
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    />
  );
}

/** تراز ۲/۴/۶/۸ ستونی — `columns` picks the form, same as the screen. */
export function TrialBalanceColumnsPrintPage() {
  const { query } = useQuery(['fromDate', 'toDate', 'levelSequence']);
  const [params] = useSearchParams();
  const raw = Number(params.get('columns') ?? '8');
  const columns = ([2, 4, 6, 8] as const).includes(raw as 2 | 4 | 6 | 8)
    ? (raw as 2 | 4 | 6 | 8)
    : 8;
  const groups = COLUMN_GROUPS[columns];
  return (
    <FinancePrintFrame<TrialBalanceColumnsResponse>
      title={`تراز ${columns.toLocaleString('fa-IR')} ستونی`}
      backPath="/finance/reports/trial-balance-columns"
      backLabel="بازگشت به تراز"
      path={`/api/v1/finance/reports/trial-balance-columns?${query}`}
      lines={(d) => [
        rangeLine(d.fromDate, d.toDate),
        d.levelSequence ? `سطح ${d.levelSequence.toLocaleString('fa-IR')}` : 'حساب‌های قابل ثبت',
      ]}
      body={(d, unit) => {
        const amount = (r: TrialBalanceColumnAmounts, k: keyof TrialBalanceColumnAmounts) =>
          formatRialFa(r[k], unit);
        return (
          <>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...cell, textAlign: 'right' }} rowSpan={2}>
                    کد
                  </th>
                  <th style={{ ...cell, textAlign: 'right' }} rowSpan={2}>
                    عنوان حساب
                  </th>
                  {groups.map((g) => (
                    <th key={g.title} colSpan={2} style={{ ...cell, textAlign: 'center' }}>
                      {g.title}
                    </th>
                  ))}
                </tr>
                <tr style={{ borderBottom: '2px solid #333' }}>
                  {groups.flatMap((g) => [
                    <th key={`${g.title}-d`} style={num}>
                      بدهکار
                    </th>,
                    <th key={`${g.title}-c`} style={num}>
                      بستانکار
                    </th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.accountRef} style={{ borderBottom: '1px solid #e2e2e2' }}>
                    <td className="ltr sk-num" style={cell}>
                      {r.accountRef}
                    </td>
                    <td style={cell}>
                      {r.accountName}
                      {r.contraryToNature ? ' *' : ''}
                    </td>
                    {groups.flatMap((g) => [
                      <td key={`${g.title}-d`} className="sk-num" style={num}>
                        {amount(r, g.debit)}
                      </td>,
                      <td key={`${g.title}-c`} className="sk-num" style={num}>
                        {amount(r, g.credit)}
                      </td>,
                    ])}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                  <td style={cell} colSpan={2}>
                    جمع
                  </td>
                  {groups.flatMap((g) => [
                    <td key={`${g.title}-d`} className="sk-num" style={num}>
                      {amount(d.totals, g.debit)}
                    </td>,
                    <td key={`${g.title}-c`} className="sk-num" style={num}>
                      {amount(d.totals, g.credit)}
                    </td>,
                  ])}
                </tr>
              </tfoot>
            </table>
            <p style={{ fontSize: 10.5, color: '#666', margin: 0 }}>
              {d.balanced ? 'تراز متوازن است' : 'تراز نامتوازن است'}
              {d.rows.some((r) => r.contraryToNature) ? ' · * مانده خلاف ماهیت حساب' : ''}
            </p>
          </>
        );
      }}
    />
  );
}

function StatementRows(props: {
  readonly title: string;
  readonly rows: readonly { accountRef: string; accountName: string; amount: string }[];
  readonly totalLabel: string;
  readonly total: string;
  readonly unit: AmountDisplayUnit;
  readonly extra?: { label: string; amount: string };
}) {
  return (
    <>
      <tr>
        <td style={{ ...cell, fontWeight: 700, paddingTop: 12 }} colSpan={2}>
          {props.title}
        </td>
      </tr>
      {props.rows.map((r) => (
        <tr key={r.accountRef} style={{ borderBottom: '1px solid #eee' }}>
          <td style={{ ...cell, paddingInlineStart: 20 }}>
            {r.accountRef} {r.accountName}
          </td>
          <td className="sk-num" style={num}>
            {signedRial(r.amount, props.unit)}
          </td>
        </tr>
      ))}
      {props.extra ? (
        <tr style={{ borderBottom: '1px solid #eee' }}>
          <td style={{ ...cell, paddingInlineStart: 20 }}>{props.extra.label}</td>
          <td className="sk-num" style={num}>
            {signedRial(props.extra.amount, props.unit)}
          </td>
        </tr>
      ) : null}
      <tr style={{ borderTop: '1px solid #333', fontWeight: 600 }}>
        <td style={cell}>{props.totalLabel}</td>
        <td className="sk-num" style={num}>
          {signedRial(props.total, props.unit)}
        </td>
      </tr>
    </>
  );
}

export function IncomeStatementPrintPage() {
  const { query, values } = useQuery(['fromDate', 'toDate']);
  return (
    <FinancePrintFrame<IncomeStatementResponse>
      title="صورت سود و زیان"
      backPath="/finance/reports/income-statement"
      backLabel="بازگشت به صورت سود و زیان"
      path={
        values.fromDate && values.toDate
          ? `/api/v1/finance/reports/income-statement?${query}`
          : null
      }
      lines={(d) => [rangeLine(d.fromDate, d.toDate)]}
      body={(d, unit) => (
        <table style={{ ...tableStyle, maxWidth: '170mm' }}>
          <tbody>
            <StatementRows
              title="درآمدها"
              rows={d.revenue.map((r) => ({ ...r, amount: r.amountRial }))}
              totalLabel="جمع درآمدها"
              total={d.totals.revenueRial}
              unit={unit}
            />
            <StatementRows
              title="هزینه‌ها"
              rows={d.expense.map((r) => ({ ...r, amount: r.amountRial }))}
              totalLabel="جمع هزینه‌ها"
              total={d.totals.expenseRial}
              unit={unit}
            />
            <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
              <td style={cell}>
                {BigInt(d.totals.netIncomeRial) >= 0n ? 'سود خالص' : 'زیان خالص'}
              </td>
              <td className="sk-num" style={num}>
                {signedRial(d.totals.netIncomeRial, unit)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    />
  );
}

export function BalanceSheetPrintPage() {
  const { query, values } = useQuery(['asOfDate']);
  return (
    <FinancePrintFrame<BalanceSheetResponse>
      title="ترازنامه"
      backPath="/finance/reports/balance-sheet"
      backLabel="بازگشت به ترازنامه"
      path={values.asOfDate ? `/api/v1/finance/reports/balance-sheet?${query}` : null}
      lines={(d) => [`در تاریخ ${displayJalaliDate(d.asOfDate)}`]}
      body={(d, unit) => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <table style={tableStyle}>
            <tbody>
              <StatementRows
                title="دارایی‌ها"
                rows={d.assets.map((r) => ({ ...r, amount: r.balanceRial }))}
                totalLabel="جمع دارایی‌ها"
                total={d.totals.assetsRial}
                unit={unit}
              />
            </tbody>
          </table>
          <table style={tableStyle}>
            <tbody>
              <StatementRows
                title="بدهی‌ها"
                rows={d.liabilities.map((r) => ({ ...r, amount: r.balanceRial }))}
                totalLabel="جمع بدهی‌ها"
                total={d.totals.liabilitiesRial}
                unit={unit}
              />
              <StatementRows
                title="حقوق صاحبان سهام"
                rows={d.equity.map((r) => ({ ...r, amount: r.balanceRial }))}
                totalLabel="جمع حقوق صاحبان سهام"
                total={d.totals.equityWithCurrentPeriodRial}
                unit={unit}
                extra={{
                  label: 'سود (زیان) سال جاری — بسته‌نشده',
                  amount: d.currentPeriodNetIncomeRial,
                }}
              />
              <tr style={{ borderTop: '2px solid #333', fontWeight: 700 }}>
                <td style={cell}>جمع بدهی‌ها و حقوق صاحبان سهام</td>
                <td className="sk-num" style={num}>
                  {signedRial(
                    (
                      BigInt(d.totals.liabilitiesRial) +
                      BigInt(d.totals.equityWithCurrentPeriodRial)
                    ).toString(),
                    unit,
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    />
  );
}
